import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Vehicle } from '@otw/models/inventory/inventory';
import DealApprovalVehicleCard from '@/views/SuperAdmin/DealApproval/components/VehicleCard';
import { usePermissions } from 'hooks/auth/usePermissions';
import { DealApproval } from '@otw/models/deals';

interface DealApprovalWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateDeal: () => void;
  onContinueAnyway: () => void;
  vehicles: Vehicle[];
  loading?: boolean;
  inventoryType?: 'master_inventory' | 'shield_inventory';
  existingDeal?: DealApproval | null;
}

const DealApprovalWarningDialog: React.FC<DealApprovalWarningDialogProps> = ({
  open,
  onClose,
  onCreateDeal,
  onContinueAnyway,
  vehicles,
  loading = false,
  inventoryType = 'master_inventory',
  existingDeal = null,
}) => {
  const { checkPermission } = usePermissions();
  const canViewPurchasePrice = checkPermission('inventory', 'view_purchase_price');
  const canViewProfit = checkPermission('inventory', 'view_profit');

  const canCreateDeal = !existingDeal;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Deal Approval Required
        </Box>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 700, maxHeight: '80vh', overflow: 'auto' }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Not all selected vehicles have been approved. Each vehicle must be individually approved (or conditionally approved) in the Deal Approval page before generating invoices.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please ensure all {vehicles.length} selected vehicle{vehicles.length !== 1 ? 's' : ''} have an approved or conditionally approved decision in the Deal Approval page before proceeding.
        </Typography>

        {existingDeal && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
              A deal already exists for these vehicles.
            </Typography>
            <Typography variant="body2">
              Each vehicle must be individually approved in the Deal Approval page before generating invoices.
            </Typography>
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Vehicles that need an approved or conditionally approved decision ({vehicles.length}):
          </Typography>
          <Box sx={{ mt: 2, pb: 2 }}>
            <Grid container spacing={2}>
              {vehicles.map(vehicle => (
                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                  <DealApprovalVehicleCard
                    collectionName={inventoryType}
                    vehicle={vehicle}
                    canViewPurchasePrice={canViewPurchasePrice}
                    canViewProfit={canViewProfit}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {!existingDeal && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can create a deal approval request for these vehicles, or continue anyway if you're certain this is correct.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onContinueAnyway}
          variant="outlined"
          disabled={loading}
        >
          Continue Anyway
        </Button>
        <Button
          onClick={onCreateDeal}
          variant="contained"
          disabled={loading || !canCreateDeal}
          startIcon={loading ? undefined : <CheckCircleIcon />}
          color="primary"
          title={!canCreateDeal ? 'A deal already exists for these vehicles' : ''}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              Creating...
            </Box>
          ) : (
            'Get Approved'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DealApprovalWarningDialog;
